// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useState } from 'react';
import WorkspaceService from '../../../../../services/workspace/WorkspaceService';
import { useDatasets } from '../../../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../../../state/hooks/OrganizationHooks';
import {
  useCurrentScenarioDatasetList,
  useCurrentScenarioId,
  useCurrentScenarioParametersValues,
} from '../../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId } from '../../../../../state/hooks/WorkspaceHooks';
import { useGestionActivites } from '../../../../../state/wizard/WizardHooks';
import { DatasetsUtils } from '../../../../../utils';

export const useRulesSet = () => {
  const scenarioId = useCurrentScenarioId();
  const currentScenarioParametersValues = useCurrentScenarioParametersValues();
  const datasetId = useCurrentScenarioDatasetList()[0];
  const workspaceId = useWorkspaceId();
  const datasets = useDatasets();
  const organizationId = useOrganizationId();
  const gestionActivites = useGestionActivites(scenarioId);
  const [isLoading, setIsLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const getRulesSetTableId = useCallback(
    (gisement) => {
      const rulesSetId = gestionActivites.find((gestion) => gestion.Gisement === gisement)?.EnsembleRegles;
      return ![undefined, null, ''.trim()].includes(rulesSetId) ? rulesSetId : null;
    },
    [gestionActivites]
  );

  const getRulesSetId = useCallback(
    (gisement) => getRulesSetTableId(gisement) ?? `${gisement}_RulesSet`,
    [getRulesSetTableId]
  );

  const getRulesSetDatasetId = useCallback(
    (gisement) => {
      const rulesSetId = getRulesSetTableId(gisement);
      const technicalPoliciesParameter = currentScenarioParametersValues?.find(
        (parameter) => parameter.parameterId === 'TechnicalPoliciesWizard'
      );
      if (technicalPoliciesParameter == null) return null;

      try {
        const parameters = JSON.parse(technicalPoliciesParameter.value);
        return parameters?.find((parameter) => parameter.parameterId === rulesSetId)?.value;
      } catch (error) {
        console.error('Technical policies parameter values parsing failed', error);
        return null;
      }
    },
    [currentScenarioParametersValues, getRulesSetTableId]
  );

  const downloadFileFromStorage = useCallback(
    (selectedGisement) => {
      setIsLoading(true);
      const datasetPartId = getRulesSetDatasetId(selectedGisement);
      let storageFilePath;
      if (datasetPartId != null) {
        const dataset = datasets?.find((item) => item.id === datasetPartId);
        storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
      } else {
        const rulesSetTableId = getRulesSetTableId(selectedGisement);
        storageFilePath = rulesSetTableId ? `dataset/${datasetId}/${rulesSetTableId}.csv` : null;
      }
      if (!storageFilePath) {
        setFileError("Le fichier associé n'existe pas");
        return;
      }
      return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePath);
    },
    [datasetId, datasets, getRulesSetDatasetId, getRulesSetTableId, organizationId, workspaceId]
  );

  return {
    getRulesSetTableId,
    getRulesSetId,
    getRulesSetDatasetId,
    downloadFileFromStorage,
    isLoading,
    setIsLoading,
    fileError,
    setFileError,
  };
};
